import React, { useCallback, useMemo, forwardRef } from 'react';
import ListItem from '@material-ui/core/ListItem';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import { PropTypes } from 'prop-types';
import { useIntl } from 'react-intl';
import { useIsBreakpointDown } from '~/modules/common/hooks';
import {
  CertificateIcon,
  DelaStarIcon
} from '~/modules/common/components/Icons';
import { useMeContext } from '~/modules/me/useMeContext';
import SkillRating from '~/modules/resourcing/common/components/Skills/SkillRating';
import DelaRecommendationButtons from '~/modules/common/components/DelaRecommendationButtons';
import SkillCustomMetadataReadOnly from './SkillCustomMetadataReadOnly';

const useStyles = makeStyles(theme => ({
  root: {
    '&:last-child': {
      borderBottom: 'none'
    },
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    paddingTop: 4,
    paddingBottom: 4
  },
  skillBarStarContainer: {
    marginTop: '-3px',
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 80
  },
  skillHeader: {
    padding: theme.spacing(0, 1, 0, 0),
    flexGrow: 0,
    flexShrink: 0,
    flexDirection: 'row',
    flexWrap: 'nowrap',
    display: 'flex',
    alignItems: 'center'
  },
  skillName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexGrow: 1,
    flexShrink: 1,
    paddingLeft: theme.spacing(0.5)
  },
  certificateIcon: {
    color: theme.palette.text.secondary,
    marginBottom: -10
  },
  certificateContainer: {
    width: theme.spacing(3)
  },
  skillMetadataDetails: {
    paddingLeft: theme.spacing(3.5)
  }
}));

const useGridStyles = makeStyles(theme => ({
  root: {
    '&:last-child': {
      borderBottom: 'none'
    },
    display: 'grid',
    gridTemplateColumns: `min-content minmax(0, 1fr)`,
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: [['"leftTray skillHeader"', '". skillCustomMetadata"']],
    columnGap: theme.spacing(0.5)
  },
  skillBarStarContainer: {
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 80
  },
  skillHeader: ({ isPsaPraaUserProfileNudgesEnabled }) => ({
    gridArea: 'skillHeader',
    padding: theme.spacing(0, 1, 0, 0),
    display: 'flex',
    flexDirection: 'row',
    flexWrap: isPsaPraaUserProfileNudgesEnabled ? 'wrap' : 'nowrap',
    alignItems: 'center'
  }),
  skillName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexGrow: 1,
    flexShrink: 1
  },
  leftTrayContainer: {
    gridArea: 'leftTray',
    display: 'flex',
    minWidth: theme.spacing(3),
    columnGap: theme.spacing(0.5),
    marginRight: theme.spacing(0.5)
  },
  skillMetadataDetails: {
    gridArea: 'skillCustomMetadata'
  }
}));

const useDelaStarIconStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(0, 0.5),
    fill: theme.palette.Dela.darkIcon,
    fontSize: theme.spacing(1.8)
  }
}));

export const SkillDetails = forwardRef(
  (
    {
      skillAssignment,
      skillAssignment: { certificate },
      certificates,
      maxSkillLevel,
      category,
      canEditSkills,
      onAcceptUserNudges,
      onRejectUserNudges,
      dataQeId
    },
    ref
  ) => {
    const {
      featureFlags: { isPsaPraaUserProfileNudgesEnabled }
    } = useMeContext();
    const intl = useIntl();
    const classes = useStyles();
    const gridClasses = useGridStyles({ isPsaPraaUserProfileNudgesEnabled });
    const delaStarIconClasses = useDelaStarIconStyles();
    const isBreakpointDownMd = useIsBreakpointDown('md');

    const skillCustomMetadataClasses = useMemo(
      () => ({
        skillMetadataDetails: isPsaPraaUserProfileNudgesEnabled
          ? gridClasses.skillMetadataDetails
          : classes.skillMetadataDetails
      }),
      [classes, gridClasses, isPsaPraaUserProfileNudgesEnabled]
    );

    const onAccept = useCallback(
      () =>
        onAcceptUserNudges({
          suggestions: [{ skillAssignments: [skillAssignment], category }]
        }),
      [onAcceptUserNudges, skillAssignment, category]
    );

    const onReject = useCallback(
      () =>
        onRejectUserNudges([{ skillAssignments: [skillAssignment], category }]),
      [onRejectUserNudges, skillAssignment, category]
    );

    const cert =
      certificate &&
      certificates &&
      certificates.find(p => p.uri === certificate);
    const title =
      skillAssignment.skillLevel.rating > 0
        ? `${intl.formatMessage({
            id: 'skillInfoCard.proficiencyLevel'
          })}: ${skillAssignment.skillLevel.rating}`
        : intl.formatMessage({ id: 'skillInfoCard.unrated' });

    return isPsaPraaUserProfileNudgesEnabled ? (
      <ListItem className={gridClasses.root} disableGutters>
        <div className={gridClasses.leftTrayContainer}>
          {skillAssignment.isDelaRecommendation && (
            <DelaRecommendationButtons
              onAccept={onAccept}
              onReject={onReject}
            />
          )}
          {cert && (
            <div className={classes.certificateContainer}>
              <Tooltip title={cert.name}>
                <CertificateIcon className={classes.certificateIcon} />
              </Tooltip>
            </div>
          )}
        </div>
        <div data-qe-id={dataQeId} className={gridClasses.skillHeader}>
          <div className={gridClasses.skillName}>
            {skillAssignment.displayText}
            {skillAssignment.isDelaRecommendation && (
              <DelaStarIcon classes={delaStarIconClasses} />
            )}
          </div>
          {Boolean(maxSkillLevel) && (
            <Tooltip title={title}>
              <div className={gridClasses.skillBarStarContainer}>
                <SkillRating
                  dataQeId={`${skillAssignment.displayText}_SkillRating`}
                  skillAssignment={skillAssignment}
                  editable={false}
                  maxSkillLevel={maxSkillLevel}
                  size={
                    isPsaPraaUserProfileNudgesEnabled && isBreakpointDownMd
                      ? 'small'
                      : undefined
                  }
                />
              </div>
            </Tooltip>
          )}
        </div>
        {skillAssignment &&
          skillAssignment.skillCustomMetadata &&
          skillAssignment.skillCustomMetadata.length > 0 && (
            <SkillCustomMetadataReadOnly
              {...skillAssignment}
              classes={skillCustomMetadataClasses}
            />
          )}
      </ListItem>
    ) : (
      <ListItem className={classes.root} disableGutters>
        <div data-qe-id={dataQeId} className={classes.skillHeader}>
          <div className={classes.certificateContainer}>
            {cert && (
              <Tooltip title={cert.name}>
                <CertificateIcon className={classes.certificateIcon} />
              </Tooltip>
            )}
          </div>
          <div className={classes.skillName}>{skillAssignment.displayText}</div>
          {Boolean(maxSkillLevel) && (
            <Tooltip title={title}>
              <SkillRating
                dataQeId={`${skillAssignment.displayText}_SkillRating`}
                className={classes.skillBarStarContainer}
                skillAssignment={skillAssignment}
                editable={false}
                maxSkillLevel={maxSkillLevel}
              />
            </Tooltip>
          )}
        </div>
        {skillAssignment &&
          skillAssignment.skillCustomMetadata &&
          skillAssignment.skillCustomMetadata.length > 0 && (
            <SkillCustomMetadataReadOnly
              {...skillAssignment}
              classes={skillCustomMetadataClasses}
            />
          )}
      </ListItem>
    );
  }
);

SkillDetails.propTypes = {
  dataQeId: PropTypes.string,
  skillAssignment: PropTypes.object.isRequired,
  certificates: PropTypes.array,
  maxSkillLevel: PropTypes.number,
  category: PropTypes.object,
  canEditSkills: PropTypes.bool,
  onAcceptUserNudges: PropTypes.func,
  onRejectUserNudges: PropTypes.func
};

export default SkillDetails;
